// import { useMatomo } from 

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { KyuLevel, Operation, Problem } from "./types";
import { kyuLevels } from "./config/kyuLevels";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { useMatomo } from "./hooks/useMatomo";
import ProblemTable from "./ProblemTable";

export const PracticeApp: React.FC = () => {
    const { trackPageView, trackEvent } = useMatomo();
    const { level, operation, seed: urlSeed } = useParams<{
        level?: string;
        operation?: string;
        seed?: string;
    }>();
    const navigate = useNavigate();

    // Derive state from URL - single source of truth
    const selectedLevel = level ? parseInt(level) : 15;
    const seed = urlSeed || '1';

    const [problems, setProblems] = useState<{ [key: string]: Problem[] }>({});
    const [showAnswers, setShowAnswers] = useState<{ [key: string]: boolean[] }>({});
    const [highlightOperation, setHighlightOperation] = useState<Operation | null>(null);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isAnzanMode, setIsAnzanMode] = useState(false);

    // Hash a string seed with level into a numeric value for RNG initialization
    const hashSeed = (str: string, level: number): number => {
        // Combine seed with level to ensure different sequences per level
        const combinedSeed = `${str}-${level}`;
        let hash = 0;
        for (let i = 0; i < combinedSeed.length; i++) {
            hash = ((hash << 5) - hash) + combinedSeed.charCodeAt(i);
            hash = hash & hash;
        }
        return Math.abs(hash);
    };

    /**
     * Linear Congruential Generator (LCG) for seeded random numbers.
     * Uses modulo to prevent overflow and ensure state stays within 32-bit range.
     * @param state - Current RNG state
     * @returns Tuple of [random value 0-1, next state] for reproducible sequences
     */
    const seededRandom = (state: number): [number, number] => {
        // Ensure state is within valid range
        const safeState = Math.abs(state) % 4294967296;
        const newState = (safeState * 1664525 + 1013904223) % 4294967296;
        return [newState / 4294967296, newState];
    };

    const generateNumber = (digits: number, currentState: number): [number, number] => {
        const min = Math.pow(10, digits - 1);
        const max = Math.pow(10, digits) - 1;
        const [random, nextState] = seededRandom(currentState);
        const num = Math.floor(random * (max - min + 1)) + min;
        return [num, nextState];
    };

    const selectDigitCount = (range: [number, number], currentState: number): [number, number] => {
        const [random, nextState] = seededRandom(currentState);
        const digits = Math.floor(random * (range[1] - range[0] + 1)) + range[0];
        return [digits, nextState];
    };

    /**
     * Box-Muller transform for normal distribution, clamped to range.
     * @param range - Min and max digit count
     * @param currentState - Current RNG state
     * @returns Tuple of [selected digit count, next state]
     */
    const selectDigitCountBellCurve = (range: [number, number], currentState: number): [number, number] => {
        const [r1, state1] = seededRandom(currentState);
        const [r2, state2] = seededRandom(state1);
        const normal = Math.sqrt(-2 * Math.log(r1)) * Math.cos(2 * Math.PI * r2);
        const mid = (range[0] + range[1]) / 2;
        const spread = (range[1] - range[0]) / 6; // Tighter clustering
        const value = Math.round(mid + normal * spread);
        const clamped = Math.max(range[0], Math.min(range[1], value));
        return [clamped, state2];
    };

    const selectDigitCountsWithCombinedLimit = (
        range1: [number, number],
        range2: [number, number],
        maxCombined: number,
        currentState: number
    ): [number, number, number] => {
        let attempts = 0;
        let digits1, digits2, state = currentState;

        do {
            [digits1, state] = selectDigitCount(range1, state);
            [digits2, state] = selectDigitCount(range2, state);
            attempts++;
        } while (digits1 + digits2 > maxCombined && attempts < 20);

        // Fallback: force compliance with combined limit
        if (digits1 + digits2 > maxCombined) {
            digits1 = Math.min(digits1, maxCombined - range2[0]);
            digits2 = maxCombined - digits1;
        }

        return [digits1, digits2, state];
    };

    const generateProblemsForOperation = (operation: Operation, level: KyuLevel, initialRngState: number): [Problem[], number] => {
        const count = level.operationCounts[operation] || Math.ceil(level.problemCount / level.operations.length);
        const problems: Problem[] = [];
        let currentRngState = initialRngState;
        const levelIndex = kyuLevels.findIndex(l => l?.level === level.level);

        for (let i = 0; i < count; i++) {
            let num1, num2;

            if (operation === 'multiplication' && level.mulDigits) {
                const [digits1, digits2, newState] = selectDigitCountsWithCombinedLimit(
                    level.mulDigits.multiplicand,
                    level.mulDigits.multiplier,
                    level.mulDigits.maxCombined,
                    currentRngState
                );
                [num1, currentRngState] = generateNumber(digits1, newState);
                [num2, currentRngState] = generateNumber(digits2, currentRngState);
            } else if (operation === 'division' && level.divDigits) {
                const [dividendDigits, state1] = selectDigitCountBellCurve(level.divDigits.dividend, currentRngState);
                const [divisorDigits, state2] = selectDigitCountBellCurve(level.divDigits.divisor, state1);

                [num2, currentRngState] = generateNumber(divisorDigits, state2);

                // Prevent division by zero
                if (num2 === 0) {
                    num2 = 1;
                }

                // For advanced levels (3rd-1st Kyu), allow remainders
                // For lower levels, ensure whole number division by generating quotient first
                if (levelIndex < 12) {
                    const quotientDigits = dividendDigits - divisorDigits;
                    const [quotient, nextState] = generateNumber(Math.max(1, quotientDigits), currentRngState);
                    num1 = quotient * num2;
                    currentRngState = nextState;
                } else {
                    [num1, currentRngState] = generateNumber(dividendDigits, currentRngState);
                }
            } else {
                [num1, currentRngState] = generateNumber(level.addSubDigits1, currentRngState);
                [num2, currentRngState] = generateNumber(level.addSubDigits2, currentRngState);
            }

            if (operation === 'subtraction') {
                // 11th Kyu: Focus on 5's Complements
                if (level.level === '11th Kyu') {
                    // Generate problems like 5-1, 5-2, 5-3, 5-4
                    num1 = 5;
                    [num2, currentRngState] = seededRandom(currentRngState);
                    num2 = Math.floor(num2 * 4) + 1; // 1 to 4
                }
                // 10th Kyu: Focus on 10's Complements
                else if (level.level === '10th Kyu') {
                    // Generate problems like 10-1, 10-2, ..., 10-9
                    num1 = 10;
                    [num2, currentRngState] = seededRandom(currentRngState);
                    num2 = Math.floor(num2 * 9) + 1; // 1 to 9
                }
                // For levels below 7th Kyu (more complex borrowing), ensure num1 > num2
                else if (levelIndex < 8 && num1 < num2) {
                    [num1, num2] = [num2, num1];
                }
                // 3rd Kyu and above can have negative results
                // CRITICAL: Full complement logic (10's complement representation) is not yet implemented
                // Per authentic curriculum, students must:
                //   1. Calculate using borrowed power of 10 (e.g., 100 + 23 - 61 = 62)
                //   2. Record the complement (62) with notation indicating it represents -38
                //   3. Track the borrowed value through multi-step problems
                // Currently allows negative results but shows "-38" instead of "62 (complement)"
                // TODO: Implement complement calculation, display notation, and answer validation
                else if (levelIndex >= 12) {
                    // Allow num1 < num2 for negative results
                } else {
                    if (num1 < num2) [num1, num2] = [num2, num1];
                }
            }

            // 4th Kyu and above can have decimals (up to 2 decimal places)
            // Curriculum requires proper unit rod placement and decimal handling
            // TODO: revisit this decimal generation approach
            if (levelIndex >= 1 && levelIndex <= 4 && operation !== 'division') {
                let r1, r2, r3;
                [r1, currentRngState] = seededRandom(currentRngState);
                [r2, currentRngState] = seededRandom(currentRngState);
                [r3, currentRngState] = seededRandom(currentRngState);

                // Randomly apply 1 or 2 decimal places to operands
                if (r1 > 0.6) {
                    const decimalPlaces = r3 > 0.5 ? 2 : 1;
                    num1 = parseFloat((num1 / Math.pow(10, decimalPlaces)).toFixed(decimalPlaces));
                }
                if (r2 > 0.6) {
                    const decimalPlaces = r3 > 0.5 ? 2 : 1;
                    num2 = parseFloat((num2 / Math.pow(10, decimalPlaces)).toFixed(decimalPlaces));
                }

                // Re-check subtraction constraint after decimal conversion
                if (operation === 'subtraction' && levelIndex === 4 && num1 < num2) {
                    [num1, num2] = [num2, num1];
                }
            }

            problems.push({ num1, num2, operation });
        }
        return [problems, currentRngState];
    };

    // Initialize URL if not present
    useEffect(() => {
        if (!level || !urlSeed) {
            const currentLevel = level ? parseInt(level) : 15;
            const currentSeed = urlSeed || '1';
            navigate(`/${currentLevel}/all/${currentSeed}`, { replace: true });
        }
    }, [level, urlSeed, navigate]);

    // Parse operation highlighting from URL
    useEffect(() => {
        if (operation && ['addition', 'subtraction', 'multiplication', 'division'].includes(operation)) {
            setHighlightOperation(operation as Operation);
            setHasScrolled(false); // Reset scroll state when operation changes
        } else {
            setHighlightOperation(null);
        }
    }, [operation]);

    // Scroll to highlighted operation after problems are generated (only once on initial load)
    useEffect(() => {
        if (highlightOperation && Object.keys(problems).length > 0 && !hasScrolled) {
            const element = document.querySelector(`[data-testid="problem-table-${highlightOperation}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setHasScrolled(true);
            }
        }
    }, [highlightOperation, problems, hasScrolled]);

    // Update URL navigation functions
    const navigateToLevel = (level: number) => {
        navigate(`/${level}/all/${seed}`, { replace: true });
    };

    const navigateToSeed = (newSeed: string) => {
        navigate(`/${selectedLevel}/all/${newSeed}`, { replace: true });
    };

    const generateRandomSeed = () => {
        const newSeed = Date.now().toString();
        navigateToSeed(newSeed);
        trackEvent('Practice', 'Generate Random Seed');
    };

    const generateAnzanProblems = (level: string, initialRngState: number): { [key: string]: Problem[] } => {
        const problems: { [key: string]: Problem[] } = { addition: [] };
        let currentRngState = initialRngState;
        const levelIndex = kyuLevels.findIndex(l => l?.level === level);

        // Anzan progression per curriculum:
        // 10th Kyu: 1 digit, 3 numbers (optional)
        // 9th-8th Kyu: 1-2 digits, 4 numbers
        // 7th Kyu: 2 digits, 5 numbers (mandatory)
        // 6th Kyu: 2 digits, 7 numbers
        // 5th Kyu+: 3 digits, 5 numbers

        let digits = 1;
        let numCount = 3;

        if (levelIndex >= 9) { // 6th Kyu+
            digits = 2;
            numCount = 7;
        } else if (levelIndex >= 8) { // 7th Kyu
            digits = 2;
            numCount = 5;
        } else if (levelIndex >= 6) { // 9th-8th Kyu
            digits = 2;
            numCount = 4;
        }

        // For 5th Kyu and above, use 3 digits
        if (levelIndex >= 10) {
            digits = 3;
            numCount = 5;
        }

        // Generate 10 Anzan problems (each is a chain of additions/subtractions)
        // Add bounds checking to ensure sums stay within reasonable mental calculation limits
        const maxSum = Math.pow(10, digits + 1) - 1; // e.g., 3 digits -> max sum 9999

        for (let i = 0; i < 10; i++) {
            const numbers: number[] = [];
            let attempts = 0;
            const maxAttempts = 100;

            // Regenerate if sum exceeds bounds
            while (attempts < maxAttempts) {
                numbers.length = 0;
                let totalSum = 0;

                for (let j = 0; j < numCount; j++) {
                    let num;
                    [num, currentRngState] = generateNumber(digits, currentRngState);
                    numbers.push(num);
                    totalSum += num;
                }

                // Check if sum is within reasonable bounds
                if (totalSum <= maxSum) {
                    break;
                }
                attempts++;
            }

            // Store as a problem where num1 is the first number and num2 is the sum of the rest
            // This represents: num1 + numbers[1] + numbers[2] + ... = answer
            const sum = numbers.slice(1).reduce((acc, n) => acc + n, 0);
            problems.addition.push({
                num1: numbers[0],
                num2: sum,
                operation: 'addition'
            });
        }

        // M/D for 6th kyu+ (2x1 for 6th, 3x1 for 5th+)
        if (levelIndex >= 9) {
            problems['multiplication'] = [];
            problems['division'] = [];

            const mulDigits1 = levelIndex >= 10 ? 3 : 2;
            const mulDigits2 = 1;
            const divDigits1 = levelIndex >= 10 ? 4 : 3;
            const divDigits2 = 1;

            for (let i = 0; i < 10; i++) {
                let num1, num2;
                [num1, currentRngState] = generateNumber(mulDigits1, currentRngState);
                [num2, currentRngState] = generateNumber(mulDigits2, currentRngState);
                problems.multiplication.push({ num1, num2, operation: 'multiplication' });

                let d1, d2;
                [d1, currentRngState] = generateNumber(divDigits1, currentRngState);
                [d2, currentRngState] = generateNumber(divDigits2, currentRngState);
                if (d2 === 0) d2 = 1;
                problems.division.push({ num1: d1 * d2, num2: d2, operation: 'division' });
            }
        }

        return problems;
    };

    const generateAllProblems = useCallback(() => {
        let currentRngState = hashSeed(seed, selectedLevel);
        const level = kyuLevels[selectedLevel];
        if (!level) return;

        const newProblems: { [key: string]: Problem[] } = {};
        const newShowAnswers: { [key: string]: boolean[] } = {};

        if (isAnzanMode) {
            const anzanProblems = generateAnzanProblems(level.level, currentRngState);
            newProblems['addition'] = anzanProblems.addition;
            newShowAnswers['addition'] = new Array(anzanProblems.addition.length).fill(false);
            if (anzanProblems.multiplication) {
                newProblems['multiplication'] = anzanProblems.multiplication;
                newShowAnswers['multiplication'] = new Array(anzanProblems.multiplication.length).fill(false);
            }
            if (anzanProblems.division) {
                newProblems['division'] = anzanProblems.division;
                newShowAnswers['division'] = new Array(anzanProblems.division.length).fill(false);
            }
        } else {
            level.operations.forEach(operation => {
                const [operationProblems, nextRngState] = generateProblemsForOperation(operation, level, currentRngState);
                newProblems[operation] = operationProblems;
                newShowAnswers[operation] = new Array(operationProblems.length).fill(false);
                currentRngState = nextRngState;
            });
        }

        setProblems(newProblems);
        setShowAnswers(newShowAnswers);
    }, [seed, selectedLevel, isAnzanMode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        generateAllProblems();
    }, [generateAllProblems]);

    const handleLevelClick = (level: number) => {
        const levelData = kyuLevels[level];
        if (!levelData) return;

        trackPageView(`Soroban Practice - ${levelData.level}`);
        trackEvent('Navigation', 'Level Change', levelData.level);
        navigateToLevel(level);
        // Reset Anzan mode if switching to a level below 7th Kyu
        if (level > 7) {
            setIsAnzanMode(false);
        }
    };

    const getOperationSymbol = (op: Operation): string => {
        switch (op) {
            case 'addition': return '+';
            case 'subtraction': return '-';
            case 'multiplication': return '×';
            case 'division': return '÷';
        }
    };

    const calculateAnswer = (problem: Problem): string | number => {
        const getDecimalPlaces = (n: number): number => {
            const s = n.toString();
            const dot = s.indexOf('.');
            return dot === -1 ? 0 : s.length - dot - 1;
        };
        const precision = problem.operation === 'multiplication'
            ? getDecimalPlaces(problem.num1) + getDecimalPlaces(problem.num2)
            : Math.max(getDecimalPlaces(problem.num1), getDecimalPlaces(problem.num2));

        switch (problem.operation) {
            case 'addition': return parseFloat((problem.num1 + problem.num2).toFixed(precision));
            case 'subtraction': return parseFloat((problem.num1 - problem.num2).toFixed(precision));
            case 'multiplication': return parseFloat((problem.num1 * problem.num2).toFixed(precision));
            case 'division': {
                const quotient = Math.floor(problem.num1 / problem.num2);
                const remainder = problem.num1 % problem.num2;
                return remainder === 0 ? quotient : `${quotient} R${remainder}`;
            }
        }
    };

    const toggleAnswer = (operation: Operation, index: number) => {
        const newShowAnswers = { ...showAnswers };
        const wasShown = newShowAnswers[operation][index];
        newShowAnswers[operation][index] = !wasShown;
        setShowAnswers(newShowAnswers);

        if (!wasShown) {
            trackEvent('Practice', 'Show Answer', operation);
        }
    };

    return (
        <div className="container mx-auto p-4 max-w-7xl">
            <img src="/soroban-header-thin.jpg" alt="Soroban" className="w-full mb-4 rounded" />
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-4xl font-bold">Soroban Practice (Beta)</h1>
                <Link to="/about" className="text-sm text-muted-foreground hover:underline">About</Link>
            </div>

            <div className="flex gap-2 items-end mb-6">
                <div className="flex-1">
                    <Label htmlFor="seed-input">Seed</Label>
                    <Input
                        id="seed-input"
                        type="text"
                        value={seed}
                        onChange={(e) => navigateToSeed(e.target.value)}
                        aria-describedby="seed-description"
                    />
                    <span id="seed-description" className="sr-only">
                        Enter a seed value for reproducible problem sets
                    </span>
                </div>
                <Button
                    onClick={generateRandomSeed}
                    variant="outline"
                    aria-label="Generate random seed"
                >
                    Random Seed
                </Button>
            </div>

            <Tabs value={selectedLevel.toString()} onValueChange={(value) => handleLevelClick(parseInt(value))}>
                <TabsList className="w-full flex-wrap h-auto">
                    {kyuLevels.slice(1).reverse().filter((level): level is KyuLevel => level !== undefined).map((level) => (
                        <TabsTrigger key={level.level} value={level.level.match(/\d+/)?.[0] || ''}>
                            {level.level}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {kyuLevels.slice(1).reverse().filter((level): level is KyuLevel => level !== undefined).map((level) => {
                    const kyuNumber = parseInt(level.level.match(/\d+/)?.[0] || '15');
                    return (
                        <TabsContent key={level.level} value={kyuNumber.toString()}>
                            <Card>
                                <CardHeader>
                                    <CardTitle>{level.level}</CardTitle>
                                    <CardDescription>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {level.operations.map(op => (
                                                <Badge
                                                    key={op}
                                                    variant="secondary"
                                                    className="cursor-pointer hover:bg-secondary/80"
                                                    onClick={() => navigate(`/${selectedLevel}/${op}/${seed}`)}
                                                >
                                                    {op}
                                                </Badge>
                                            ))}
                                        </div>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        {level.operations.includes('subtraction') ? 'Add/Sub' : 'Add'}: {level.addSubDigits1}×{level.addSubDigits2} digits
                                        {level.mulDigits && ` | Multiply: ${level.mulDigits.multiplicand}×${level.mulDigits.multiplier} digits`}
                                        {level.divDigits && ` | Divide: ${level.divDigits.dividend}×${level.divDigits.divisor} digits`}
                                    </p>
                                    <div className="flex gap-2">
                                        {kyuNumber <= 7 && (
                                            <Button
                                                onClick={() => {
                                                    const newMode = !isAnzanMode;
                                                    setIsAnzanMode(newMode);
                                                    trackEvent('Practice', 'Toggle Mode', newMode ? 'Anzan' : 'Soroban');
                                                }}
                                                variant="outline"
                                                aria-pressed={isAnzanMode}
                                            >
                                                {isAnzanMode ? 'Switch to Soroban Mode' : 'Switch to Anzan Mode'}
                                            </Button>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    );
                })}
            </Tabs>

            {isAnzanMode && <h2 className="text-2xl font-semibold mt-6 mb-4">Anzan (Mental Calculation) Practice</h2>}
            {Object.keys(problems).length > 0 && (
                <div className="mt-6 space-y-4" role="region" aria-label="Practice problems">
                    {Object.keys(problems).map(operation => (
                        problems[operation as Operation] && (
                            <ProblemTable
                                key={operation}
                                operation={operation as Operation}
                                problems={problems[operation as Operation]}
                                showAnswers={showAnswers[operation as Operation]}
                                toggleAnswer={toggleAnswer}
                                getOperationSymbol={getOperationSymbol}
                                calculateAnswer={calculateAnswer}
                                highlight={highlightOperation === operation}
                                currentLevel={selectedLevel}
                                currentSeed={seed}
                            />
                        )
                    ))}
                </div>
            )}
        </div>
    );
};
